// Looping MIDI clip player for the synth.
//
// Parses Standard MIDI Files via @tonejs/midi, flattens every track's
// notes into a single timeline, and drives `synth.noteOn / noteOff`
// directly. The engine handles mono reduction internally (legato via
// the held-stack allocator), so the player is now a pure passthrough —
// it doesn't need to know whether the synth is in mono or poly mode.
//
// Loop boundary calls `allNotesOff()` so we don't leave voices stuck
// from sustained notes that crossed the wrap point.

import { Midi } from "@tonejs/midi";

export interface SynthBridge {
  noteOn(midi: number, velocity?: number): void;
  noteOff(midi: number): void;
  /** Release every sounding voice. Used at loop boundaries. */
  allNotesOff(): void;
}

/** Internal: a single on or off event on the flattened timeline. */
interface TimelineEvent {
  /** Absolute time in seconds from clip start. */
  time: number;
  type: "on" | "off";
  midi: number;
  velocity: number;
}

export class MidiPlayer {
  private events: TimelineEvent[] = [];
  private endTime = 0;
  /** Tempo embedded in the loaded clip (first tempo meta event, or
   *  @tonejs/midi's default of 120 if none). The current playback
   *  speed is `synthBpm / fileBpm`, so a 100-bpm file with the tempo
   *  knob at 120 plays 1.2× faster, at 60 plays 0.6× slower. */
  private fileBpm = 120;
  private synthBpm = 120;

  private playing = false;
  /** Virtual playhead in seconds. Advanced per tick by `wallDelta × speed`
   *  so that tempo changes during playback don't produce time
   *  discontinuities. Reset on play() and at the loop boundary. */
  private virtualElapsedSec = 0;
  private lastTickWallMs = 0;
  private cursor = 0;
  private rafHandle = 0;

  constructor(private synth: SynthBridge) {}

  /** Set the playback tempo from the synth's global BPM knob. Takes
   *  effect immediately; the virtual clock keeps its current position
   *  and just advances at the new rate from this point on. */
  setBpm(bpm: number): void {
    this.synthBpm = bpm > 0 ? bpm : 120;
  }

  private currentSpeed(): number {
    return this.fileBpm > 0 ? this.synthBpm / this.fileBpm : 1.0;
  }

  /** Fetch and load a clip from a URL relative to the deployed site. */
  async loadFromUrl(url: string): Promise<void> {
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`failed to fetch ${url}: ${resp.status}`);
    const bytes = await resp.arrayBuffer();
    this.loadFromBytes(bytes);
  }

  /** Load a clip from an in-memory MIDI byte buffer (e.g. user upload). */
  loadFromBytes(bytes: ArrayBuffer): void {
    const midi = new Midi(bytes);

    // The file's nominal BPM is the first tempo event's bpm; @tonejs/midi
    // defaults to 120 if no tempo meta is present. Tempo *changes* baked
    // into note timestamps survive the speed scaling because they're
    // proportional — the user's knob is a tempo multiplier on the whole
    // file, not an override of internal rubato.
    this.fileBpm = midi.header.tempos[0]?.bpm ?? 120;

    const events: TimelineEvent[] = [];
    let maxEnd = 0;
    for (const track of midi.tracks) {
      for (const note of track.notes) {
        const start = note.time;
        const end = note.time + note.duration;
        if (end > maxEnd) maxEnd = end;
        events.push({ time: start, type: "on", midi: note.midi, velocity: note.velocity });
        events.push({ time: end,   type: "off", midi: note.midi, velocity: 0 });
      }
    }

    // Time-order events. At the same instant we want "on" before "off"
    // so that mono-legato in the engine sees the new note while the old
    // is still held (which keeps the envelope's stage instead of
    // entering release).
    events.sort((a, b) => {
      if (a.time !== b.time) return a.time - b.time;
      if (a.type !== b.type) return a.type === "on" ? -1 : 1;
      return 0;
    });

    this.events = events;
    this.endTime = maxEnd;
  }

  hasClip(): boolean {
    return this.events.length > 0;
  }

  isPlaying(): boolean {
    return this.playing;
  }

  play(): void {
    if (!this.hasClip()) return;
    this.synth.allNotesOff();
    this.cursor = 0;
    this.virtualElapsedSec = 0;
    this.lastTickWallMs = performance.now();
    this.playing = true;
    this.scheduleNextTick();
  }

  stop(): void {
    this.playing = false;
    if (this.rafHandle !== 0) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = 0;
    }
    this.synth.allNotesOff();
  }

  private scheduleNextTick(): void {
    this.rafHandle = requestAnimationFrame(() => this.tick());
  }

  private tick(): void {
    if (!this.playing) return;

    const now = performance.now();
    const dtSec = (now - this.lastTickWallMs) / 1000;
    this.lastTickWallMs = now;
    this.virtualElapsedSec += dtSec * this.currentSpeed();

    while (
      this.cursor < this.events.length &&
      this.events[this.cursor]!.time <= this.virtualElapsedSec
    ) {
      this.fire(this.events[this.cursor]!);
      this.cursor++;
    }

    if (this.virtualElapsedSec >= this.endTime && this.cursor >= this.events.length) {
      // Loop boundary: kill any sustained voices then restart from t=0.
      // Carry the fractional overshoot into the next loop so very fast
      // playback rates don't drop events that fall in the wrap window.
      this.synth.allNotesOff();
      this.cursor = 0;
      this.virtualElapsedSec -= this.endTime;
      while (
        this.cursor < this.events.length &&
        this.events[this.cursor]!.time <= this.virtualElapsedSec
      ) {
        this.fire(this.events[this.cursor]!);
        this.cursor++;
      }
    }

    this.scheduleNextTick();
  }

  private fire(ev: TimelineEvent): void {
    if (ev.type === "on") {
      this.synth.noteOn(ev.midi, ev.velocity);
    } else {
      this.synth.noteOff(ev.midi);
    }
  }
}
